import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
/* eslint-disable  import/no-cycle */
import { RootState } from 'store/rootReducer';
import { AppThunk } from 'store/store';
import keyBy from 'lodash/keyBy';
import set from 'lodash/set';
import { ApiErrors, Loaders } from 'api/types';
import { UNAUTHORIZED, FORBIDDEN, NOT_FOUND, UNPROCESSABLE_ENTITY } from 'http-status-codes';
import { i18n } from 'helpers/i18n';
import { axiosInstance, axiosS3Instance } from '../axiosInstance';

const documentsApiPrefix = '/documents';

export type Document = {
  created_at: string;
  extension: string;
  id: string;
  link_id: string;
  link_type: string;
  name: string;
  updated_at: string;
  size: number;
};

export type DocumentsState = {
  apiErrors: ApiErrors;
  downloadError: string;
  list: {
    [key: string]: Document;
  };
  loaders: Loaders;
  pagination: {
    index: number;
    indexesCount: number;
    size: number;
  };
  searchTerm?: string;
  upload: {
    file: string;
    progress: number;
  };
};

const initialState: DocumentsState = {
  apiErrors: {},
  downloadError: '',
  loaders: {},
  list: {},
  pagination: {
    size: 30,
    index: 1,
    indexesCount: 0
  },
  upload: {
    progress: 0,
    file: ''
  }
};

type DocumentSearchParams = {
  id?: string;
  pagination?: {
    index: number;
    size?: number;
  };
  search?: string;
  type?: string;
};

type FetchDocumentsResponse = {
  documents: Document[];
  pagination: {
    index: number;
    indexesCount: number;
    size: number;
  };
  searchTerm: string;
};

export const fetchDocuments = createAsyncThunk<FetchDocumentsResponse, DocumentSearchParams, { rejectValue: string }>(
  'documents/fetchDocuments',
  async ({ id, type, pagination, search }, { rejectWithValue }) => {
    try {
      const url = !id && !type ? `/documents/all` : `/documents/${type}/${id}`;
      const response = await axiosInstance.get(url, { params: { ...pagination, search } });
      const {
        data: { documents, size, index, indexes_count }
      } = response;
      return {
        documents,
        pagination: { size, index, indexesCount: indexes_count },
        searchTerm: search || ''
      };
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      if (status === UNAUTHORIZED || status === FORBIDDEN) {
        return rejectWithValue(i18n.t('errors.unauthorized'));
      }
      if (status === NOT_FOUND) {
        return rejectWithValue(i18n.t('cannot find documents'));
      }
      return rejectWithValue(i18n.t('errors.something went wrong'));
    }
  }
);

export const deleteDocument = createAsyncThunk<string, string, { rejectValue: string }>(
  'documents/delete',
  async (documentId, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${documentsApiPrefix}/${documentId}`);
      return documentId;
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      switch (status) {
        case FORBIDDEN:
        case UNAUTHORIZED:
          return rejectWithValue(i18n.t('errors.you cannot delete this document'));
        case NOT_FOUND:
          return rejectWithValue(i18n.t('errors.document not found'));
        default:
          return rejectWithValue(i18n.t('errors.something went wrong'));
      }
    }
  }
);

export const unlinkFromResource = createAsyncThunk<
  string,
  { documentId: string; linkId: string; linkType: string },
  { rejectValue: string }
>('documents/unlink', async ({ documentId, linkType, linkId }, { rejectWithValue }) => {
  try {
    await axiosInstance.delete(`${documentsApiPrefix}/${documentId}/links/${linkType}/${linkId}`);
    return documentId;
  } catch (err) {
    if (!err.response) {
      return rejectWithValue(i18n.t('errors.something went wrong'));
    }
    const { status } = err.response;
    switch (status) {
      case FORBIDDEN:
      case UNAUTHORIZED:
        return rejectWithValue(i18n.t('errors.you cannot unlink this document'));
      case NOT_FOUND:
        return rejectWithValue(i18n.t('errors.document not found'));
      default:
        return rejectWithValue(i18n.t('errors.something went wrong'));
    }
  }
});

export const updateDocument = createAsyncThunk<Document, { id: string; name: string }, { rejectValue: string }>(
  'documents/update',
  async ({ id, name }, { dispatch, rejectWithValue }) => {
    let response;
    dispatch(updateListLoader({ id, value: true }));
    try {
      response = await axiosInstance.put(`${documentsApiPrefix}/${id}/rename`, { name });
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      switch (status) {
        case FORBIDDEN:
        case UNAUTHORIZED:
          return rejectWithValue(i18n.t('errors.you cannot rename this document'));
        case NOT_FOUND:
          return rejectWithValue(i18n.t('errors.document not found'));
        case UNPROCESSABLE_ENTITY:
          return rejectWithValue(i18n.t('errors.invalid document name'));
        default:
          return rejectWithValue(i18n.t('errors.something went wrong'));
      }
    } finally {
      dispatch(updateListLoader({ id, value: true }));
    }

    const { data } = response;
    return data;
  }
);

const documentsSlice = createSlice({
  initialState,
  name: 'documents',
  reducers: {
    setIsSaving: (state, { payload }: PayloadAction<boolean>) => {
      state.loaders.isSaving = payload;
    },
    setDownloadError: (state, { payload }: PayloadAction<string>) => {
      state.downloadError = payload;
    },
    resetDocuments: (state: DocumentsState) => {
      state.list = {};
      state.searchTerm = '';
      state.pagination = {
        index: 1,
        indexesCount: 0,
        size: 30
      };
    },
    setUploadProgress: (state: DocumentsState, { payload }: PayloadAction<{ file: string; progress: number }>) => {
      const { file, progress } = payload;
      state.upload.file = file;
      state.upload.progress = progress;
    },
    updateDocumentList: (state: DocumentsState, { payload }: PayloadAction<Document>) => {
      state.list[`${payload.id}`] = payload;
    },
    closeError: state => {
      state.apiErrors = {};
    },
    updateListLoader: (state, { payload }: PayloadAction<{ id: string; value: boolean }>) => {
      const { id, value } = payload;
      if (value) {
        set(state, `loaders.savingErrorItems.${id}`, '');
      }
      set(state, `loaders.isSavingItems.${id}`, value);
    }
  },
  extraReducers: builder =>
    builder
      .addCase(fetchDocuments.fulfilled, (state, { payload }: PayloadAction<FetchDocumentsResponse>) => {
        const { documents, pagination, searchTerm } = payload;
        state.list = keyBy(documents, 'id');
        state.pagination = pagination;
        state.searchTerm = searchTerm;
        state.loaders.isLoading = false;
      })
      .addCase(fetchDocuments.pending, state => {
        state.loaders.isLoading = true;
        state.apiErrors.loadingError = '';
      })
      .addCase(fetchDocuments.rejected, (state, { payload }) => {
        state.loaders.isLoading = false;
        state.apiErrors.loadingError = payload || i18n.t('errors.something went wrong');
      })
      .addCase(deleteDocument.fulfilled, (state, { payload }) => {
        state.loaders.isDeleting = false;
        delete state.list[`${payload}`];
      })
      .addCase(deleteDocument.pending, state => {
        state.loaders.isDeleting = true;
        state.apiErrors.deletingError = '';
      })
      .addCase(deleteDocument.rejected, (state, { payload }) => {
        state.loaders.isDeleting = false;
        state.apiErrors.deletingError = payload || i18n.t('errors.something went wrong');
      })
      .addCase(unlinkFromResource.fulfilled, (state, { payload }) => {
        state.loaders.isDeleting = false;
        delete state.list[`${payload}`];
      })
      .addCase(unlinkFromResource.pending, state => {
        state.loaders.isDeleting = true;
        state.apiErrors.deletingError = '';
      })
      .addCase(unlinkFromResource.rejected, (state, { payload }) => {
        state.loaders.isDeleting = false;
        state.apiErrors.deletingError = payload || i18n.t('errors.something went wrong');
      })
      .addCase(updateDocument.fulfilled, (state, { payload }) => {
        state.loaders.isUpdating = false;
        state.list[`${payload.id}`] = payload;
      })
      .addCase(updateDocument.pending, state => {
        state.loaders.isUpdating = true;
        state.apiErrors.updatingError = '';
      })
      .addCase(updateDocument.rejected, (state, { payload }) => {
        state.loaders.isUpdating = false;
        state.apiErrors.updatingError = payload || i18n.t('errors.something went wrong');
      })
});

const { actions, reducer } = documentsSlice;
export { reducer as documents };
export const {
  updateListLoader,
  closeError,
  resetDocuments,
  updateDocumentList,
  setUploadProgress,
  setIsSaving,
  setDownloadError
} = actions;

export const saveDocument = (file: File): AppThunk => async dispatch => {
  dispatch(setIsSaving(true));
  let { name } = file;
  let extension = file.type;

  const dotLocation = name.lastIndexOf('.');
  if (dotLocation < 0) {
    extension = extension.substring(extension.lastIndexOf('/') + 1);
  } else {
    extension = name.substring(dotLocation + 1);
    name = name.substring(0, dotLocation);
  }

  let response: any;
  try {
    response = await axiosInstance.post(`${documentsApiPrefix}/presigned/uploadurl`, {
      extension,
      name
    });
  } catch (e) {
    dispatch(setIsSaving(false));
    return;
  }
  const config = {
    onUploadProgress: (progressEvent: any) => {
      const { loaded, total } = progressEvent;
      if (loaded === total) {
        dispatch(
          setUploadProgress({
            file: '',
            progress: 0
          })
        );
      } else {
        const progress = Math.round((loaded * 100) / total);
        dispatch(
          setUploadProgress({
            file: `${name}.${extension}`,
            progress
          })
        );
      }
    }
  };
  const {
    data,
    data: { upload_url }
  } = response;
  try {
    await axiosS3Instance.put(upload_url, file, config);
  } catch (e) {
    await axiosInstance.delete(`${documentsApiPrefix}/${response.id}/permanently`);
    dispatch(setIsSaving(false));
    return;
  }

  dispatch(updateDocumentList(data));
  dispatch(setIsSaving(false));
};

export const downloadDocument = (id: string): AppThunk => async dispatch => {
  let response: any;
  try {
    response = await axiosInstance.get(`${documentsApiPrefix}/${id}/downloadurl`);
  } catch (err) {
    if (!err.response) {
      dispatch(setDownloadError(i18n.t('errors.something went wrong, please retry')));
    }
    const { status } = err.response;
    switch (status) {
      case FORBIDDEN:
      case UNAUTHORIZED:
        dispatch(setDownloadError(i18n.t('errors.you cannot download this document')));
        break;
      case NOT_FOUND:
        dispatch(setDownloadError(i18n.t('errors.document not found')));
        break;
      default:
        return dispatch(setDownloadError(i18n.t('errors.something went wrong')));
    }
  }

  const {
    data: { url }
  } = response;
  window.open(url, '_blank');
};

export const getDocumentsList = (state: RootState) => state.documents.list;
export const getUploadProgress = (state: RootState) => state.documents.upload;
export const getDocumentLoaders = (state: RootState) => state.documents.loaders;
export const getDocumentApiErrors = (state: RootState) => state.documents.apiErrors;
export const getDocumentsPagination = (state: RootState) => state.documents.pagination;
export const getDocumentsSearchTerm = (state: RootState) => state.documents.searchTerm;
